/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { observer } from "mobx-react";
import DialerServiceData from "Stores/DialerServiceStore";
import { toasterState } from "Components/common/toaster";
import Utils from "Utils/utils";
import { voBizBuyNumberPopupState } from "Pages/Dashboard/settings/VoBizBuyNumberPopup";
import { buyNewNumberPopupState } from "Pages/Dashboard/settings/BuyNewNumberPopup";
import countryData from "country-telephone-data";
import Flags from "Assets/png/flags.png";
import PhoneNumberDropdown from "./PhoneNumberDropdown";
import "react-telephone-input/css/default.css";

const getCountryFromPhoneNumber = phoneNumber => {
  if (!phoneNumber) return null;

  const { allCountries, allCountryCodes, iso2Lookup } = countryData;
  const cleanNumber = phoneNumber.replace(/[^\d+]/g, "");
  const inputNumberForCountries = cleanNumber.replace(/^\+/, "").substr(0, 4);

  if (allCountryCodes[inputNumberForCountries]) {
    const iso2 = allCountryCodes[inputNumberForCountries][0];
    if (iso2 && iso2Lookup[iso2] !== undefined) {
      const country = allCountries[iso2Lookup[iso2]];
      if (country) return country;
    }
  }

  let bestMatch = { dialCode: "", priority: 10001 };

  allCountries.forEach(country => {
    const numberToCheck = cleanNumber.startsWith("+")
      ? cleanNumber.replace(/^\+/, "")
      : cleanNumber;
    const dialCodeToCheck = country.dialCode.replace(/^\+/, "");

    if (numberToCheck.startsWith(dialCodeToCheck)) {
      if (
        dialCodeToCheck.length > bestMatch.dialCode.replace(/^\+/, "").length
      ) {
        bestMatch = country;
      } else if (
        dialCodeToCheck.length ===
          bestMatch.dialCode.replace(/^\+/, "").length &&
        country.priority < bestMatch.priority
      ) {
        bestMatch = country;
      }
    }
  });

  if (!bestMatch || !bestMatch.name || bestMatch.priority === 10001) {
    return null;
  }

  return bestMatch;
};

const CountryFlag = ({ country }) => {
  if (!country || !country.iso2) return null;

  return (
    <div className="react-tel-input">
      <div
        className={`flag ${country.iso2.toLowerCase()}`}
        style={{ backgroundImage: `url(${Flags})` }}
      ></div>
    </div>
  );
};

const DialerDefaultNumber = props => {
  const { calling = false, isVoBiz = false } = props;
  const [loader, setLoader] = useState(true);
  const [phoneNoList, setPhoneNoList] = useState([]);
  const [defaultNo, setDefaultNo] = useState("");
  const [showDropdownMenu, setDropdownMenu] = useState(false);

  // Which store fields to read from depends on channel
  const storeDefaultNo = isVoBiz ? DialerServiceData.voBizDefaultNo : DialerServiceData.dialerDefaultNo;
  const storePhoneList = isVoBiz ? DialerServiceData.voBizPhoneList : DialerServiceData.dialerPhoneList;

  const showDropdownPhoneList = () => {
    setDropdownMenu(true);
  };

  const hideDefaultNoList = () => {
    setDropdownMenu(false);
  };

  const updateDefaultNo = (item) => {
    toasterState.setToastMsg("Please wait...", "info");
    if (isVoBiz) {
      DialerServiceData.updateVoBizDefaultPhoneNumber({ ph_number: item.value });
    } else {
      DialerServiceData.dialerBtnLoader = true;
      DialerServiceData.updateDefaultPhoneNumber({ ph_number: item.value });
      setLoader(true);
    }
    setDropdownMenu(false);
  };

  /**
   * Keeps the displayed caller ID honest for the CURRENT channel.
   *
   * This used to assign only when the store value was truthy and keyed only on that
   * value, so switching to a channel whose list had never loaded (null, not
   * "purchaseNumber") left the previous channel's number on screen — an India DID
   * displayed while the Twilio channel was selected, which that channel cannot dial
   * from. It also meant a failed fetch left the loader spinning forever with no message.
   *
   * The server is the authority: for India the number VoBiz actually dials with comes
   * from member_vobiz.fromNumber, so what is shown must always be the store's value for
   * the channel in view.
   */
  const syncDefaultNoForChannel = async isCancelled => {
    if (storeDefaultNo) {
      setDefaultNo(storeDefaultNo);
      setLoader(false);
      return;
    }
    // Nothing known for this channel yet — clear immediately so the other channel's
    // number cannot linger, then load this channel's list.
    setDefaultNo("");
    setLoader(true);
    try {
      if (isVoBiz) {
        await DialerServiceData.getVoBizPhoneNumbers();
      } else {
        await DialerServiceData.getDialerPhoneList(false);
      }
    } catch (e) {
      console.error("Error loading numbers for the selected channel", e);
    }
    if (isCancelled()) return;
    const latest = isVoBiz
      ? DialerServiceData.voBizDefaultNo
      : DialerServiceData.dialerDefaultNo;
    // Fall back to the purchase prompt rather than an endless loader: the rep gets a
    // next action instead of a grey box.
    setDefaultNo(latest || "purchaseNumber");
    setLoader(false);
  };

  const constructDropdownList = phoneList => {
    const newPhoneList = [];
    phoneList?.length &&
      phoneList.map((item, index) => {
        // item can be a string (US numbers) or an object {number, configured} (VoBiz numbers)
        const numStr = typeof item === "string" ? item : item?.number;
        if (!numStr) return;
        let newObj = {
          value: numStr,
          key: `phoneNumber${index + 1}`,
          country: getCountryFromPhoneNumber(numStr)
        };
        newPhoneList.push(newObj);
      });
    setPhoneNoList(newPhoneList);
  };

  // The CTA must match the channel in view: sending a rep on the Twilio channel to the
  // India buy-number flow (or the reverse) hands them a number the channel cannot use.
  const purchaseNumberRedirect = () => {
    if (!Utils.isAdmin()) {
      toasterState.setToastMsg(
        isVoBiz
          ? "No India calling number purchased. Please contact your account administrator."
          : "No calling number available. Please contact your account administrator.",
        "fail"
      );
      return;
    }
    if (isVoBiz) {
      voBizBuyNumberPopupState.setShowPopup(true);
    } else {
      buyNewNumberPopupState.setShowPopup(true);
    }
  };

  useEffect(() => {
    if (storePhoneList && storePhoneList.length > 0) {
      constructDropdownList(storePhoneList);
    }
  }, [storePhoneList]);

  useEffect(() => {
    if (!isVoBiz) {
      setLoader(DialerServiceData.dialerBtnLoader);
    }
  }, [DialerServiceData.dialerBtnLoader]);

  useEffect(() => {
    let cancelled = false;
    syncDefaultNoForChannel(() => cancelled);
    return () => {
      cancelled = true;
    };
    // isVoBiz belongs here: the channel changing is exactly when a stale number shows.
  }, [storeDefaultNo, isVoBiz]);

  const countryInfo = getCountryFromPhoneNumber(defaultNo);

  return (
    <div className="dsDefaultNoSection">
      <div className="dsWrapDefautContact">
        {!loader && (
          <>
            {defaultNo && defaultNo !== "purchaseNumber" && (
              <div
                onClick={() => {
                  !calling && showDropdownPhoneList();
                }}
                className="numberSection"
              >
                <div className="text">Calling from: </div>
                <div className="number">
                  {countryInfo && <CountryFlag country={countryInfo} />}
                  <span className="numberText">{defaultNo}</span>
                  {!calling && (
                    <i className="material-icons">keyboard_arrow_up</i>
                  )}
                </div>
              </div>
            )}
            {defaultNo === "purchaseNumber" && (
              <>
                <div className="text">Calling from: </div>
                <div onClick={purchaseNumberRedirect} className="purchaseNumber">
                  {isVoBiz ? "Buy India Number" : "Add Number"}
                </div>
              </>
            )}
          </>
        )}
        {loader && (
          <>
            <div className="text">Calling from: </div>
            <span className="defaultNoLoader linear-background"></span>
          </>
        )}
        {showDropdownMenu && (
          <PhoneNumberDropdown
            phoneNumbers={phoneNoList}
            selectedNumber={defaultNo}
            onSelect={updateDefaultNo}
            onClose={hideDefaultNoList}
          />
        )}
      </div>
    </div>
  );
};
export default withRouter(observer(DialerDefaultNumber));
export { DialerDefaultNumber };

